import axios from '../helpers/axios';
import { productOfficeConstants as C } from './constants';

const errMsg = (e, def = 'Ошибка запроса') =>
    e?.response?.data?.message || e?.response?.data?.error || e?.message || def;

const ensureOfficeId = (explicit, getState) => {
    const fromState = getState()?.office?.currentOfficeId;
    const fromLS = localStorage.getItem('ao_currentOfficeId');
    const officeId = explicit || fromState || fromLS;

    if (!officeId) throw new Error('officeId обязателен');

    return officeId;
};

const withOfficeRequestConfig = (officeId, extra = {}) => {
    const {
        params,
        headers,
        ...rest
    } = extra;

    return {
        ...rest,
        params: {
            officeId,
            ...(params || {}),
        },
        ...(headers ? { headers } : {}),
    };
};

export const listMyProducts = (officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.LIST_MINE_REQUEST });

        const res = await axios.get(
            '/office/product/mine',
            withOfficeRequestConfig(oid)
        );

        const products = res.data?.products || [];

        dispatch({
            type: C.LIST_MINE_SUCCESS,
            payload: products,
        });

        return {
            ok: true,
            data: products,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.LIST_MINE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const listOfficeProducts = (officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.LIST_OFFICE_REQUEST });

        const res = await axios.get(
            '/office/product/office',
            withOfficeRequestConfig(oid)
        );

        const products = res.data?.products || [];

        dispatch({
            type: C.LIST_OFFICE_SUCCESS,
            payload: products,
        });

        return {
            ok: true,
            data: products,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.LIST_OFFICE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const listAvailableProducts = (officeId, options = {}) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);
        const categoryId = options?.categoryId || options?.category || '';

        dispatch({ type: C.LIST_AVAILABLE_REQUEST });

        const res = await axios.get(
            '/office/product/available',
            withOfficeRequestConfig(oid, {
                params: {
                    ...(categoryId ? { category: categoryId } : {}),
                },
            })
        );

        const products = res.data?.products || [];

        dispatch({
            type: C.LIST_AVAILABLE_SUCCESS,
            payload: products,
        });

        return {
            ok: true,
            data: products,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.LIST_AVAILABLE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const bulkAddAvailableProductsToOffice = (officeId, payload = {}) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);
        const categoryId = payload.categoryId || payload.category || '';
        const productIds = Array.isArray(payload.productIds) ? payload.productIds : [];

        dispatch({ type: C.PRODUCT_OFFICE_BULK_ADD_REQUEST });

        const res = await axios.post(
            '/office/product/available/bulk-add',
            {
                officeId: oid,
                categoryId,
                productIds,
                visibleForCustomers: payload.visibleForCustomers !== false,
                enabled: payload.enabled !== false,
            },
            withOfficeRequestConfig(oid)
        );

        dispatch({
            type: C.PRODUCT_OFFICE_BULK_ADD_SUCCESS,
            payload: {
                addedCount: res.data?.addedCount || 0,
                updatedCount: res.data?.updatedCount || 0,
                totalCount: res.data?.totalCount || 0,
                productIds: res.data?.productIds || [],
                message: res.data?.message || 'Товары добавлены',
            },
        });

        await Promise.all([
            dispatch(listOfficeProducts(oid)),
            dispatch(listAvailableProducts(oid, categoryId ? { categoryId } : {})),
        ]);

        return {
            ok: true,
            data: res.data || null,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.PRODUCT_OFFICE_BULK_ADD_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

function toPayload(form, officeId) {
    const hasPictures = Array.isArray(form.productPictures) && form.productPictures.length > 0;
    const hasModel = !!form.modelFile || !!form.mtlFile || (Array.isArray(form.textures) && form.textures.length > 0);

    if (hasPictures || hasModel) {
        const fd = new FormData();

        Object.entries(form).forEach(([key, value]) => {
            if (key === 'productPictures' && Array.isArray(value)) {
                value.forEach((file) => fd.append('productPictures', file));
                return;
            }

            if (key === 'textures' && Array.isArray(value)) {
                value.forEach((file) => fd.append('textures', file));
                return;
            }

            if (value !== undefined && value !== null) {
                fd.append(key, value);
            }
        });

        fd.append('officeId', officeId);

        return {
            data: fd,
            isFormData: true,
        };
    }

    return {
        data: {
            ...form,
            officeId,
        },
        isFormData: false,
    };
}

export const createOfficeProduct = (form, officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.CREATE_REQUEST });

        const {
            data,
            isFormData,
        } = toPayload(form, oid);

        const res = await axios.post(
            '/office/product/create',
            data,
            withOfficeRequestConfig(
                oid,
                isFormData
                    ? { headers: { 'Content-Type': 'multipart/form-data' } }
                    : {}
            )
        );

        dispatch({
            type: C.CREATE_SUCCESS,
            payload: {
                product: res.data?.product || null,
                message: res.data?.message || 'Товар создан',
            },
        });

        await Promise.all([
            dispatch(listMyProducts(oid)),
            dispatch(listOfficeProducts(oid)),
            dispatch(listAvailableProducts(oid)),
        ]);

        return {
            ok: true,
            data: res.data?.product || null,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.CREATE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const updateOfficeProduct = (id, patch, officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.UPDATE_REQUEST });

        const res = await axios.patch(
            `/office/product/${id}`,
            {
                ...patch,
                officeId: oid,
            },
            withOfficeRequestConfig(oid)
        );

        dispatch({
            type: C.UPDATE_SUCCESS,
            payload: {
                product: res.data?.product || null,
                message: res.data?.message || 'Товар обновлён',
            },
        });

        await Promise.all([
            dispatch(listMyProducts(oid)),
            dispatch(listOfficeProducts(oid)),
            dispatch(listAvailableProducts(oid)),
        ]);

        return {
            ok: true,
            data: res.data?.product || null,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.UPDATE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const archiveOfficeProduct = (id, officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.ARCHIVE_REQUEST });

        const res = await axios.post(
            `/office/product/${id}/archive`,
            { officeId: oid },
            withOfficeRequestConfig(oid)
        );

        dispatch({
            type: C.ARCHIVE_SUCCESS,
            payload: {
                product: res.data?.product || null,
                message: res.data?.message || 'Статус архива изменён',
            },
        });

        await Promise.all([
            dispatch(listMyProducts(oid)),
            dispatch(listOfficeProducts(oid)),
            dispatch(listAvailableProducts(oid)),
        ]);

        return {
            ok: true,
            data: res.data?.product || null,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.ARCHIVE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const toggleVisibleOfficeProduct = (id, officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.TOGGLE_VISIBLE_REQUEST });

        const res = await axios.post(
            `/office/product/${id}/visible`,
            { officeId: oid },
            withOfficeRequestConfig(oid)
        );

        dispatch({
            type: C.TOGGLE_VISIBLE_SUCCESS,
            payload: {
                product: res.data?.product || null,
                message: res.data?.message || 'Видимость обновлена',
            },
        });

        await Promise.all([
            dispatch(listMyProducts(oid)),
            dispatch(listOfficeProducts(oid)),
            dispatch(listAvailableProducts(oid)),
        ]);

        return {
            ok: true,
            data: res.data?.product || null,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.TOGGLE_VISIBLE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};

export const shareOfficeProduct = (id, payload, officeId) => async (dispatch, getState) => {
    try {
        const oid = ensureOfficeId(officeId, getState);

        dispatch({ type: C.SHARE_REQUEST });

        const res = await axios.post(
            `/office/product/${id}/share`,
            {
                ...payload,
                officeId: oid,
            },
            withOfficeRequestConfig(oid)
        );

        dispatch({
            type: C.SHARE_SUCCESS,
            payload: {
                product: res.data?.product || null,
                message: res.data?.message || 'Шаринг обновлён',
            },
        });

        await Promise.all([
            dispatch(listMyProducts(oid)),
            dispatch(listOfficeProducts(oid)),
            dispatch(listAvailableProducts(oid)),
        ]);

        return {
            ok: true,
            data: res.data?.product || null,
        };
    } catch (e) {
        const error = errMsg(e);

        dispatch({
            type: C.SHARE_FAILURE,
            payload: error,
        });

        return {
            ok: false,
            error,
        };
    }
};